/**
 * MembershipCard.js — Valle della Pace wine club membership card.
 *
 * OVERRIDES: src/components/base/MembershipCard.js
 *
 * MIGRATION PARITY (old createWineMembership, valledellapace/script.js 2846-2898;
 * data verified against the live old backend valledellapace.concierge.redaugment.com):
 *   - Cover image (clickable → detail view, like old cardClicked(msg, unique_id)).
 *   - Title.
 *   - Inner panel:
 *       • price_additional_text at the TOP (old .card-box-price, e.g.
 *         "10% discount on all wines").
 *       • the `description` ARRAY rendered as check_circle bullets (old
 *         .card-box-para list) — the 3 marketing lines, NOT bottles/curated/etc.
 *       • "Learn more" button.
 *       • Footer (row): price + price_unit on the LEFT (old
 *         .membership-price-details — price red 16px over unit), Join Now link on
 *         the RIGHT (old logged-out <a href="${cta_link}">${cta_text}</a>).
 *
 * NOTE: the old logged-IN path showed an in-widget join-club checkout
 *   (sendMessage(signup_message, clubId)); that club-signup flow is not ported
 *   yet, so we always render the external Join Now link (the logged-out path).
 *
 * Reference: valledellapace/script.js Lines 2846-2898 (createWineMembership).
 */

import { emit, getState ,isChatDisabled} from "../../../core/state.js";
import { getTheme, isMembershipEnabled } from "../../../core/configAccessors.js";
import { getToken } from "../../../features/optional/account_c7.js";
import { ASSETS_BASE_URL } from "../../../core/env.js";

/** Tenant theme.default_image (admin) → absolute URL; else bundled valle-bkg. */
function resolveDefaultMembershipImage() {
  const raw =
    getTheme("default_image") ||
    getTheme("card_logo_url") ||
    getTheme("membership_card_image") ||
    getState().tenant?.default_image ||
    null;
  if (!raw) return `${ASSETS_BASE_URL}/assets/valledellapace/valle-bkg.webp`;
  const path = String(raw).trim();
  if (!path) return `${ASSETS_BASE_URL}/assets/valledellapace/valle-bkg.webp`;
  if (/^https?:\/\//i.test(path)) return path;
  return `${ASSETS_BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
}

const BOOK_ICON =
  '<svg viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.67 13V3a1.67 1.67 0 0 1 1.66-1.67H13v13.34H4.33A1.67 1.67 0 0 1 2.67 13zm0 0a1.67 1.67 0 0 1 1.66-1.67H13"/></svg>';

// check_circle (filled), monochrome so CSS recolors via currentColor (old used
// the Material check_circle icon for each .card-box-para bullet).
const CHECK_ICON =
  '<svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm-1.2 14.6L6 11.8l1.4-1.4 3.4 3.4 6.2-6.2 1.4 1.4-7.6 7.6z"/></svg>';

const esc = (s) => String(s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

function isTrue(v) {
  return v === true || v === "true" || v === "True";
}

export default class MembershipCard {
  render(club) {
    const card = document.createElement("div");
    card.className = "ws-card ws-membership-card ws-rich-membership";

    const detailLabel = club.message || `Tell me more about ${club.title || "this membership"}`;
    // uniqueId → BE returns this membership's detail card (handle_unique_id_lookup).

    const openDetail = () =>{

          if(isChatDisabled()) return;
           emit("ws-prompt-select", { label: detailLabel, value: detailLabel, uniqueId: club.unique_id });
        }

    // Cover image — clickable → detail (old .card-box-cover-image onclick).
    // Fallback: theme.default_image (admin), then card_logo_url, then valle-bkg.
    const head = document.createElement("div");
    head.className = "ws-card-head";
    const img = document.createElement("img");
    img.className = "ws-card-img";
    const cover = String(club.cover_image_link || "").trim();
    const hasCover = !!cover;
    img.src = hasCover ? cover : resolveDefaultMembershipImage();
    if (!hasCover) img.classList.add("ws-vp-mem-logo");
    img.alt = club.title || "Membership";
    img.loading = "lazy";
    img.style.cursor = "pointer";
    img.addEventListener("click", openDetail);
    head.appendChild(img);
    card.appendChild(head);

    // Title (old .card-box-title).
    if (club.title) {
      const title = document.createElement("p");
      title.className = "ws-card-title";
      title.title = club.title;
      title.textContent = club.title;
      card.appendChild(title);
    }

    // Inner panel.
    const inner = document.createElement("div");
    inner.className = "ws-rich-inner";

    // price_additional_text at the top (old .card-box-price).
    if (club.price_additional_text) {
      const disc = document.createElement("p");
      disc.className = "ws-vp-mem-discount";
      disc.textContent = club.price_additional_text;
      inner.appendChild(disc);
    }

    // Description ARRAY → check_circle bullets (old .card-box-para list).
    const descLines = Array.isArray(club.description)
      ? club.description
      : club.description
        ? [club.description]
        : [];
    if (descLines.length) {
      const list = document.createElement("ul");
      list.className = "ws-rich-bullets";
      descLines.forEach((text) => {
        const li = document.createElement("li");
        li.innerHTML = `<span class="ws-rich-check">${CHECK_ICON}</span><span>${esc(text)}</span>`;
        list.appendChild(li);
      });
      inner.appendChild(list);
    }

    // Learn more → detail follow-up.
    const learn = document.createElement("button");
    learn.type = "button";
    learn.className = "ws-card-learnmore";
    learn.innerHTML = `<span class="ws-learnmore-icon">${BOOK_ICON}</span><span>Learn more</span>`;
    learn.addEventListener("click", openDetail);
    inner.appendChild(learn);

    // Footer (row): price + unit (left), Join Now (right) — old .innerfooter.
    const footer = document.createElement("div");
    footer.className = "ws-rich-membership-footer";

    const priceBlock = document.createElement("div");
    priceBlock.className = "ws-vp-mem-price";
    if (club.price) {
      const price = document.createElement("p");
      price.className = "ws-card-price";
      price.textContent = club.price;
      priceBlock.appendChild(price);
    }
    if (club.price_unit) {
      const unit = document.createElement("p");
      unit.className = "ws-card-unit";
      unit.textContent = club.price_unit;
      priceBlock.appendChild(unit);
    }
    footer.appendChild(priceBlock);

    const showButton = club.display_button === undefined ? true : isTrue(club.display_button);
    if (club.cta_link && showButton) {
      // Membership feature is on for Valle — real in-chat Join button
      // (login-gated), not the external link.
      if (isMembershipEnabled() && getToken()) {
        const cta = document.createElement("button");
        cta.type = "button";
        cta.className = "ws-card-cta";
        cta.textContent = club.cta_text || "Join Now";
        cta.addEventListener("click", () => {
          emit("ws-club-join", { clubId: club.clubId, club });
        });
        footer.appendChild(cta);
      } else {
        const cta = document.createElement("a");
        cta.className = "ws-card-cta";
        cta.href = club.cta_link;
        cta.target = "_blank";
        cta.rel = "noopener noreferrer";
        cta.textContent = club.cta_text || "Join Now";
        footer.appendChild(cta);
      }
    }
    inner.appendChild(footer);

    card.appendChild(inner);
    return card;
  }
}
